import { Button, Card, CardContent } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ArrowLeft, ChevronsDownUp, ChevronsUpDown, Download, Filter, MinusCircle, PlusCircle } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { LEVEL_LABELS } from "../consts";
import { downloadManagerialReport } from "./download";

const money = (n?: number | null) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
// Amounts are signed (CR negative); the DR/CR column carries the direction, so show the size here
const amt = (a?: any) => money(a?.amount === null || a?.amount === undefined ? a?.amount : Math.abs(a.amount));
const DrCr = ({ a }: { a?: any }) =>
  a?.amount === null || a?.amount === undefined || !a?.drCr ? <span className="text-gray-400">—</span>
    : <span className={a.drCr === "CR" ? "text-green-700" : "text-red-600"}>{a.drCr}</span>;
const letter = (i: number) => String.fromCharCode(65 + (i % 26));

const SECTIONS: [string, string][] = [["communityWise", "COMMUNITY"], ["zoneWise", "ZONE"], ["blockWise", "BLOCK"], ["propertyWise", "PROPERTY"]];
const INDENT: Record<string, string> = { COMMUNITY: "", ZONE: "md:ml-4", BLOCK: "md:ml-8", PROPERTY: "md:ml-12" };

/** One object's P&L (and B/S) table: category > report group > GL, with budget and unit columns per criteria. */
const NodeTable = ({ node, criteria }: { node: any; criteria: any }) => {
  const bySubGroups = criteria?.reportExpansion !== "BY_GL_ACCOUNTS";
  const withBudget = criteria?.budgets === "WITH_BUDGET";
  const withUnits = criteria?.unitDisplay !== "SUPPRESS_UNITS";
  const totalCols = (bySubGroups ? 4 : 3) + 2 + (withBudget ? 4 : 0);
  const Amounts = ({ r }: { r: any }) => (
    <>
      <td className="px-3 py-2 text-right tabular-nums">{amt(r.reportingPeriodActual)}</td>
      <td className="px-3 py-2 text-center text-xs"><DrCr a={r.reportingPeriodActual} /></td>
      {withBudget && (
        <>
          <td className="px-3 py-2 text-right tabular-nums">{amt(r.reportingPeriodBudget)}</td>
          <td className="px-3 py-2 text-center text-xs"><DrCr a={r.reportingPeriodBudget} /></td>
          <td className="px-3 py-2 text-right tabular-nums">{money(r.variance?.amount)}</td>
          <td className="px-3 py-2 text-right tabular-nums">{amt(r.annualBudget)}</td>
        </>
      )}
    </>
  );
  if (!node.categories?.length) {
    return <p className="px-4 py-6 text-sm text-gray-500">No GL accounts of this Financial Version match the selected criteria.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-sm">
        <thead>
          {withBudget && (
            <tr className="text-xs text-gray-500">
              <th colSpan={(bySubGroups ? 4 : 3)} />
              <th colSpan={2} className="px-3 pt-2 text-center font-medium">Actual ($)</th>
              <th colSpan={2} className="px-3 pt-2 text-center font-medium">Budget ($)</th>
              <th className="px-3 pt-2 text-right font-medium">Variance ($)</th>
              <th className="px-3 pt-2 text-right font-medium">Annual Budget</th>
            </tr>
          )}
          <tr className="border-b text-left text-xs text-gray-600">
            <th className="px-3 py-2 font-medium">Account Cate</th>
            {bySubGroups && <th className="px-3 py-2 font-medium">Report Grp</th>}
            <th className="px-3 py-2 font-medium">GL Account</th>
            <th className="px-3 py-2 font-medium">Description</th>
            <th className="px-3 py-2 text-right font-medium">Reporting Period Actual</th>
            <th className="px-3 py-2 text-center font-medium">DR/CR</th>
            {withBudget && (
              <>
                <th className="px-3 py-2 text-right font-medium">Reporting Period Budget</th>
                <th className="px-3 py-2 text-center font-medium">DR/CR</th>
                <th className="px-3 py-2 text-right font-medium">Variance</th>
                <th className="px-3 py-2 text-right font-medium">Annual Budget</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {node.categories.map((cat: any, ci: number) => (
            <Fragment key={cat.accountCategoryId}>
              <tr className="bg-blue-50 font-semibold text-primary">
                <td className="px-3 py-2" colSpan={bySubGroups ? 4 : 3}>{letter(ci)}. {cat.accountCategoryName}</td>
                <Amounts r={cat} />
              </tr>
              {cat.children?.map((grp: any, gi: number) => (
                <Fragment key={grp.reportingGroupId ?? `g${gi}`}>
                  {bySubGroups && grp.reportingGroupName && (
                    <tr className="bg-blue-100/60 font-medium">
                      <td className="px-3 py-2" />
                      <td className="px-3 py-2" colSpan={3}>{gi + 1}. {grp.reportingGroupName}</td>
                      <Amounts r={grp} />
                    </tr>
                  )}
                  {grp.children?.map((gl: any) => (
                    <tr key={gl.glMasterId} className="border-b border-gray-100">
                      <td className="px-3 py-2" />
                      {bySubGroups && <td className="px-3 py-2" />}
                      <td className="px-3 py-2 font-medium">{gl.glAccountNumber}</td>
                      <td className="px-3 py-2">{gl.description}</td>
                      <Amounts r={gl} />
                    </tr>
                  ))}
                </Fragment>
              ))}
              {withUnits && (
                <tr className="bg-gray-100 text-xs">
                  <td colSpan={totalCols} className="px-3 py-2">
                    <div className="flex flex-wrap gap-x-10 gap-y-1">
                      <span><span className="block text-gray-500">Total Amount</span>
                        <span className="font-semibold">{money(Math.abs(Number(cat.totalAmount ?? 0)))}</span></span>
                      <span><span className="block text-gray-500">Total Units</span><span className="font-semibold">{cat.totalUnits ?? "—"}</span></span>
                      <span><span className="block text-gray-500">Amount / Unit</span>
                        <span className="font-semibold">{money(Math.abs(Number(cat.amountPerUnit ?? 0)))}</span></span>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Managerial Reporting result (Managerial Report.docx, screens 3-5). */
const ResultView = ({ result, criteria, appliedLabels, onEditCriteria, communityName }: any) => {
  const allKeys = useMemo(
    () => SECTIONS.flatMap(([key]) => (result?.[key] || []).map((n: any) => `${n.objectType}:${n.objectId}`)),
    [result]
  );
  const [open, setOpen] = useState<Set<string>>(new Set());
  const allOpen = allKeys.length > 0 && allKeys.every((k) => open.has(k));
  const toggle = (k: string) => setOpen((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const chips: [string, any][] = [
    ["Financial Version", appliedLabels?.financialVersion],
    ["Budget Version", appliedLabels?.budgetVersion],
    ["Report Expansion", criteria?.reportExpansion === "BY_GL_ACCOUNTS" ? "By GL Accounts" : "By Sub-Groups"],
    ["Scope", criteria?.scope === "P_AND_L_BS" ? "P&L and B/S" : "P&L Only"],
    ["Unit Display", criteria?.unitDisplay === "SUPPRESS_UNITS" ? "Suppress Units" : "Report Units"],
    ["Retained Earnings", criteria?.retainedEarnings === "WITH" ? "With" : "Without"],
    ["Object Type", LEVEL_LABELS[criteria?.objectType]],
    ["Object Values", appliedLabels?.objectValues],
    ["Report Level", LEVEL_LABELS[criteria?.reportLevel]],
    [criteria?.periodBasis === "BUDGET_YEAR" ? "Budget Year" : "Entry Year", criteria?.fiscalYear],
    ["Period", `${String(criteria?.periodFrom).padStart(2, "0")} – ${String(criteria?.periodTo).padStart(2, "0")}`],
    ["Document Types", appliedLabels?.documentTypes],
    ["GL Accounts", appliedLabels?.glAccounts],
    ["Account Categories", appliedLabels?.accountCategories],
  ];
  const fileBase = `Managerial_Report_${(communityName || "community").replace(/[^A-Za-z0-9]+/g, "_")}_FY${criteria?.fiscalYear}_P${criteria?.periodFrom}-${criteria?.periodTo}`;
  const nothing = SECTIONS.every(([key]) => !(result?.[key] || []).length);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-[14px] bg-blue-50 border-blue-100 border rounded-md p-4">
        <span><span className="font-semibold">Financial Version</span> - {result?.financialTemplateName || "—"}</span>
        <span><span className="font-semibold">Budget Version</span> - {result?.budgetVersionName || "Without budgets"}</span>
        <span><span className="font-semibold">Reporting For</span> - {result?.reportingForSummary || "—"}</span>
        <span><span className="font-semibold">Units</span> - {result?.totalUnits ?? "—"}</span>
      </div>

      <div className="rounded-md border bg-white px-4 py-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-primary"><Filter className="w-3.5 h-3.5" /> Filters Applied</p>
        <div className="flex flex-wrap gap-2">
          {chips.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
            <span key={k} className="rounded-md border bg-gray-50 px-2.5 py-1 text-xs">
              <span className="block text-[10px] text-gray-500">{k}</span>{v}
            </span>
          ))}
        </div>
      </div>

      <Card className="shadow-none border-0 p-0 rounded-md">
        <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3">
          <button type="button" className="flex items-center gap-2 text-[14px] hover:text-primary" onClick={onEditCriteria}>
            <ArrowLeft className="w-4 h-4" /> Edit Selection Criteria
          </button>
          <div className="flex gap-2">
            <Button type="button" onClick={() => setOpen(allOpen ? new Set() : new Set(allKeys))} disabled={!allKeys.length}>
              {allOpen ? <><ChevronsDownUp className="w-4 h-4" /> Collapse All Sections</> : <><ChevronsUpDown className="w-4 h-4" /> Expand All Sections</>}
            </Button>
            <Button type="button" variant="outline" disabled={nothing}
              onClick={() => downloadManagerialReport(result, criteria, criteria?.downloadReport === "TSV" ? "TSV" : "CSV", fileBase)}>
              <Download className="w-4 h-4" /> Download {criteria?.downloadReport === "TSV" ? "TSV" : "CSV"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-none border-0 p-0 rounded-md">
        <CardContent className="flex flex-col gap-5 p-4">
          {nothing && <p className="text-sm text-gray-500">No objects for the selected Object Type / Values.</p>}
          {SECTIONS.map(([key, level]) => {
            const nodes = result?.[key] || [];
            if (!nodes.length) return null;
            return (
              <div key={key} className="flex flex-col gap-2">
                <h3 className="text-[15px] font-semibold">{LEVEL_LABELS[level]} Wise</h3>
                {nodes.map((node: any) => {
                  const k = `${node.objectType}:${node.objectId}`;
                  const isOpen = open.has(k);
                  return (
                    <div key={k} className={cn("rounded-lg border bg-white", INDENT[level])}>
                      <button type="button" onClick={() => toggle(k)}
                        className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg bg-blue-50/70 px-4 py-2.5 text-left text-sm hover:bg-blue-50">
                        <span className="flex items-center gap-2 font-medium">
                          {isOpen ? <MinusCircle className="w-4 h-4 text-primary" /> : <PlusCircle className="w-4 h-4 text-primary" />}
                          {node.objectName}
                        </span>
                        <span className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs md:text-sm">
                          <span><span className="font-semibold">{node.units}</span> units</span>
                          <span>Income : <span className="font-medium">{money(-Number(node.incomeTotal ?? 0))}</span></span>
                          <span>Expense : <span className="font-medium">{money(node.expenseTotal)}</span></span>
                        </span>
                      </button>
                      {isOpen && <NodeTable node={node} criteria={criteria} />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultView;
